import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AttachmentDownloadDetailDto, DocumentDownloadDisclosureAuditDto } from 'webcommon/shared';
import { AttachmentRepository, DisclosureAuditRepository } from 'webcommon/webapi';
import { ApNotificationService } from './ApNotificationService';
import { DocumentService } from './DocumentService';

@Injectable({ providedIn: 'root' })
export class AttachmentDownloadService {

  // showSaveFilePicker() is a browser API method(on window) that opens a “Save file” dialog so the user can choose
  // where to save a file, either by picking an existing file or entering a new name.
  // What it does
  //
  // Shows a native save dialog controlled by the browser, only in secure contexts(HTTPS) and with a user
  // gesture(like a button click).
  //
  // Lets you suggest a default file name(suggestedName) and restrict allowed types(via types, MIME types
  // plus extensions).
  //
  // Returns a Promise that resolves to a FileSystemFileHandle, which you can then use with the File
  // System Access API to write data to disk.
  //
  // Throws an AbortError if the user cancels the dialog, and other errors(like SecurityError) if called
  // without proper origin / user activation

  constructor(
    private readonly attachmentRepository: AttachmentRepository,
    private readonly documentService: DocumentService,
    private readonly apNotificationService: ApNotificationService,
    private readonly disclosureAuditRepository: DisclosureAuditRepository,
  ) { }

  // Sample usage
  //
 // downloadContentToFile(): void {
 //     if (this.downloadingDocument || this.attachmentDownloadDetail === undefined) { return; }
 //     this.downloadingDocument = true;
 //
 //     const handlePromise = this.attachmentDownloadDetail.SkipFilePicker
 //       ? null
 //       : this.documentService.getSaveFileHandle(this.attachmentDownloadDetail.FullFileName);
 //
 //     this.attachmentDownloadService.downloadAttachment(this.documentId, this.attachmentDownloadDetail, handlePromise).pipe(
 //       finalize(() => { this.downloadingDocument = false; }),
 //       takeUntil(this.onDestroy$),
 //     ).subscribe();
 //   }
//
 //   private prefetchAttachmentDownloadDetail(documentId: string): void {
 //     this.attachmentDownloadDetail = undefined;
 //     this.attachmentDownloadService.getAttachmentDownloadDetail(documentId).pipe(
 //       takeUntil(this.onDestroy$),
 //     ).subscribe((detail) => {
 //       this.attachmentDownloadDetail = detail;
 //     });
 //   }

  //  Call before calling before calling downloadAttachment() to pre-fetch the metadata.
  getAttachmentDownloadDetail(documentId: string): Observable<AttachmentDownloadDetailDto> {
    return this.attachmentRepository.getAttachmentDownloadDetail(documentId);
  }

  // Returns an Observable so the component can use finalize() to manage its loading flag.
  // detail must be pre-fetched before the user gesture (e.g. when the attachment list loads)
  // so that FullFileName can be passed synchronously to getSaveFileHandle() before this
  // method is called. Chrome/Edge's showSaveFilePicker() must be called within the
  // user-gesture activation window, before any async operations — calling it after an
  // HTTP request throws a SecurityError.
  //
  // Firefox and Safari do not support showSaveFilePicker; on those browsers no file picker
  // is shown and the file is downloaded silently to the browser's default downloads folder.
  //
  // Call getAttachmentDownloadDetail() to pre-fetch metadata (filename, mimetype)
  // before calling this method. The caller is responsible for creating and passing
  // handlePromise before any async work begins.
  downloadAttachment(
    documentId: string,
    downloadInfo: AttachmentDownloadDetailDto,
    handlePromise: Promise<FileSystemFileHandle> | null,
  ): Observable<void> {
    return this.resolveHandle$(handlePromise).pipe(
      switchMap((handle) => {
        return handle === 'cancelled' ? of(undefined) : this.saveContent$(documentId, downloadInfo, handle);
      }),
      catchError((_error: unknown) => {
        this.apNotificationService.error(
          'The file could not be downloaded. Please try again or contact support.',
          'Download Failed',
        );
        return of(undefined); // Swallow error so finalize() still fires in the component
      }),
    );
  }

  // Resolves the file handle promise, treating AbortError (user cancelled) as a sentinel
  // and any other error as null (fall through to <a download>).
  private resolveHandle$(handlePromise: Promise<FileSystemFileHandle> | null) {
    if (!handlePromise) { return of(null); } // showSaveFilePicker not available (Firefox/Safari)
    return from(handlePromise).pipe(
      catchError((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return of('cancelled' as const); // user dismissed the picker — skip download
        }
        return of(null); // unexpected error — fall through to <a download>
      }),
    );
  }

  private saveContent$(
    documentId: string,
    downloadInfo: AttachmentDownloadDetailDto,
    handle: FileSystemFileHandle | null,
  ): Observable<void> {
    return this.attachmentRepository.getAttachmentContentForDownload(documentId).pipe(
      switchMap((dto) => {
        // Server returns null content when the document is unavailable; treat as a no-op rather than an error.
        if (dto.ContentAsBase64 === null) { return of(undefined); }

        if (!downloadInfo.SkipFilePicker && handle) {
          // Chrome/Edge: write to the handle the user confirmed in the picker.
          // Use downloadInfo.MimeType rather than dto.MimeType — downloadInfo was pre-fetched before the
          // handle was opened and is the authoritative source for metadata.
          return from(this.documentService.writeToFileHandle(handle, dto.ContentAsBase64, downloadInfo.MimeType)).pipe(
            switchMap(() => this.auditDocumentDownload(downloadInfo)),
          );
        }

        // Firefox/Safari or SkipFilePicker: no File System Access API, so trigger an <a download> instead.
        // FullFileName and MimeType come from dto here because detail is only fetched for the
        // picker pre-population; on this path the content response is the sole data source.
        this.documentService.downloadDocumentToFile(downloadInfo.FullFileName, dto.ContentAsBase64, downloadInfo.MimeType);
        return this.auditDocumentDownload(downloadInfo);
      }),
    );
  }

  private auditDocumentDownload(downloadInfo: AttachmentDownloadDetailDto): Observable<void> {
    if (downloadInfo.PatientId == null) { return of(undefined); }
    const downloadAuditDto: DocumentDownloadDisclosureAuditDto = {
      PatientId: downloadInfo.PatientId,
      DocumentId: downloadInfo.AttachmentId,
      FileName: downloadInfo.FullFileName,
      Description: 'Document Downloaded: ' + downloadInfo.FullFileName,
    };
    return this.disclosureAuditRepository.auditDocumentDownload(downloadAuditDto).pipe(map(() => undefined));
  }
}
